// Daily-message editor — BottomSheet (W36.0, spec §2.2 R2.3 + §3.2).
//
// Spec §2.2 R2.3 asks for the DiaryEditor's shape: 工具列 + 底部動作列. This is
// that, minus the image affordance (spec §2.10 removes message images).
//
// The interactions that actually matter, per spec §3.2:
//
// - The **whole hero message block** opens this sheet, hit target ≥44px
//   (DESIGN §15). The hit target is asserted on the class, not on a measured
//   rect — jsdom has no layout engine, so `getBoundingClientRect()` returns
//   zeros for every element (W35.5 lesson).
// - Save is disabled on blank input, and the counter is the only place the
//   140 limit is surfaced (§2.2 R7.3).
// - Clearing is a separate, explicit destructive action — the sheet never
//   guesses between "you meant to empty it" and "you meant to save nothing".
//
// The emoji tray is the W36.0 decision on §10 #4: 12 fixed quick-insert
// buttons, no full picker.

import { useEffect, useRef, useState } from "react";
import { Eraser, Smile } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useDraft } from "@/lib/hooks/useDraft";
import { Button } from "@/components/ui/button";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import { DASHBOARD_I18N, QUICK_EMOJI } from "@/lib/i18n/dashboard";
import { cn } from "@/lib/utils/cn";

export const MESSAGE_MAX_LENGTH = 140;

export interface MessageEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current content, or "" when the slot is empty. */
  initialContent: string;
  /** True when a message already exists — enables the clear action. */
  hasExisting: boolean;
  onSave: (content: string) => Promise<void> | void;
  onClear: () => Promise<void> | void;
  isSaving?: boolean;
  isClearing?: boolean;
}

export function MessageEditorSheet({
  open,
  onOpenChange,
  initialContent,
  hasExisting,
  onSave,
  onClear,
  isSaving = false,
  isClearing = false,
}: MessageEditorSheetProps) {
  const { variant } = useSheetVariant();
  // W39.75 — the daily message is the product's daily ritual between two
  // people, and it had the same exposure as the diary editor: `content` lived
  // only in React state, so the redirect that a revoked session causes took the
  // sentence with it. Same probe, pointed here.
  const messageDraft = useDraft("daily-message");
  const [content, setContent] = useState(initialContent || messageDraft.value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Re-seed on open so a cancelled edit never leaks into the next open — but a
  // **draft** outranks the server's copy, because a draft only exists when
  // someone was interrupted mid-sentence. Preferring `initialContent` here is
  // how the diary editor's first draft version failed to restore.
  //
  // `messageDraft.value` is deliberately **not** a dependency: it is read once, in
  // the hook's lazy initialiser. Listing it made this effect re-fire and reset the
  // content — which is how the emoji tray's "insert at the caret" stopped
  // working, one unrelated test at a time.
  useEffect(() => {
    if (open) setContent(messageDraft.value || initialContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialContent]);

  const trimmed = content.trim();
  const isBlank = trimmed.length === 0;
  const overLimit = content.length > MESSAGE_MAX_LENGTH;
  const canSave = !isBlank && !overLimit && !isSaving;

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setContent((prev) => `${prev}${emoji}`);
      return;
    }
    // Insert at the caret rather than appending, so the tray behaves like a
    // keyboard when composing mid-sentence.
    const start = el.selectionStart ?? content.length;
    const end = el.selectionEnd ?? content.length;
    const next = `${content.slice(0, start)}${emoji}${content.slice(end)}`;
    setContent(next.slice(0, MESSAGE_MAX_LENGTH));
    requestAnimationFrame(() => {
      el.focus();
      const caret = Math.min(start + emoji.length, MESSAGE_MAX_LENGTH);
      el.setSelectionRange(caret, caret);
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    await onSave(trimmed);
    // Cleared only once it is on the server. Clearing earlier — on unmount, or
    // when the sheet closes — is what would make the draft useless for the case
    // it exists for.
    messageDraft.clear();
    onOpenChange(false);
  };

  const handleClear = async () => {
    await onClear();
    messageDraft.clear();
    onOpenChange(false);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      title={DASHBOARD_I18N.message.edit}
      // W56.0 I — a form, so a stray tap on the backdrop must not throw away
      // the message being written.
      dismissibleOnBackdrop={false}
      data-testid="message-editor-sheet"
    >
      <div className="flex flex-col gap-s-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            messageDraft.save(e.target.value);
          }}
          placeholder={DASHBOARD_I18N.message.placeholder}
          rows={4}
          maxLength={MESSAGE_MAX_LENGTH}
          aria-label={DASHBOARD_I18N.message.edit}
          data-testid="message-editor-input"
          className={cn(
            "w-full resize-none rounded-md border border-hairline bg-paper-elevated p-s-4",
            "text-body text-ink-1 placeholder:text-ink-3",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
          )}
        />

        {/* Emoji tray — fixed 12, no expand (W36.0 §10 #4). */}
        <div
          role="group"
          aria-label="快速表情符號"
          data-testid="message-emoji-tray"
          className="flex flex-wrap gap-s-1 border-t border-hairline pt-s-3"
        >
          <Smile className="mt-s-2 h-4 w-4 shrink-0 text-ink-3" aria-hidden />
          {QUICK_EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => insertEmoji(emoji)}
              aria-label={`插入 ${emoji}`}
              data-testid={`message-emoji-${emoji}`}
              // h-11 w-11 = 44px per DESIGN §15. Asserted on the class because
              // jsdom cannot measure layout.
              className="press flex h-11 w-11 items-center justify-center rounded-sm text-title-3 hover:bg-black/5"
            >
              <span aria-hidden className="emoji-font">
                {emoji}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-s-3">
          <span
            data-testid="message-counter"
            className={cn(
              "text-meta tabular-nums",
              overLimit ? "text-partner-strong" : "text-ink-3",
            )}
          >
            {DASHBOARD_I18N.message.counter.replace(
              "{count}",
              String(content.length),
            )}
          </span>
          {hasExisting ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={isClearing}
              data-testid="message-clear"
              leadingIcon={<Eraser className="h-3.5 w-3.5" />}
            >
              {DASHBOARD_I18N.message.clear}
            </Button>
          ) : null}
        </div>

        <Button
          variant="primary"
          className="w-full"
          onClick={handleSave}
          disabled={!canSave}
          data-testid="message-save"
        >
          {DASHBOARD_I18N.message.saved}
        </Button>
      </div>
    </BottomSheet>
  );
}
